import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import Select from "react-select";
import Switch from "react-switch";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faTrash, faPercent, faDollarSign, faPlane, faUserTie} from "@fortawesome/free-solid-svg-icons";
import Modal from "../modal";
import MoneyInput from "../money-input";
import Utils from "../../utils/utils";
import {fetch} from "../../utils/api-wrapper";
import {imputationDocumentId} from "../../pages/review-expense/utils/contract-expense-json";
import {resolveImputationSelectOptions} from "../../utils/expense-rule-imputation-targets";
import {
    amountBasisToUi,
    TAX_BASIS_PRETAX,
    TAX_BASIS_TAX_INCLUDED,
    uiToAmountBasis,
} from "../../utils/expense-rule-amount-basis";
import {loadXpensCategories, getXpensCategoryLabel} from "../../utils/xpens-categories";
import "./expense-rule-edit-modal.scss";

const EXPENSE_SCOPE = "expense";
const EXPENSE_ACCOUNT_SCOPE = "expense_account";
const COMPARISON_OPERATORS = ["gt", "gte", "lt", "lte", "eq"];
const IMPUTATION_LEVEL_PARENT = "parent";
const IMPUTATION_LEVEL_CHILD = "child";

const createEmptyCondition = (scope = EXPENSE_SCOPE) => ({
    type: scope === EXPENSE_ACCOUNT_SCOPE ? "monthly_submitted_report_count" : "category_spend_limit",
    parameters: {},
});

const ExpenseRuleEditModal = ({selectedRule, setSelectedRule, data, clientImputations, onSubmit, onCancel, validationError}) => {
    const localRule = selectedRule || {};
    const [rankOptions, setRankOptions] = useState([]);
    const hasAderant = Utils.hasAderantIntegration(data);

    // XPEN-3187: safe no-op if already cached by App.js's session-level fetch.
    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    // XPEN-3243: rank data only exists for firms with a live Aderant connection —
    // without it, an employee_rank rule could never match anyone. The
    // reset-to-[]-on-disconnect here matches src/pages/admin/admin-users.js's
    // loadRankOptions for this same fetch; src/pages/expenses_rates.js's
    // equivalent effect shares the boolean-dependency style but has no reset.
    useEffect(() => {
        if (!hasAderant) {
            setRankOptions([]);
            return;
        }
        fetch("/client/options/?field_name=Rank", "GET", {}, (response) => {
            setRankOptions(Utils.mapRankOptionsFromApi(response?.data));
        });
    }, [hasAderant]);

    const categoryLabel = (category) => getXpensCategoryLabel(data, category);

    const readLabel = (key, fallback) => {
        const translated = data.t.read(key);
        return translated === key ? fallback : translated;
    };

    const rankOptionsById = rankOptions.reduce((accumulator, option) => {
        accumulator[option.value] = option;
        return accumulator;
    }, {});

    // XPEN-3026-007: computed once and spread into both scope lists below so
    // they cannot drift apart — employee_rank is the first condition type
    // valid in both scopes.
    const employeeRankOption = hasAderant
        ? [{value: "employee_rank", label: data.t.read("expense_rules.employee_rank")}]
        : [];

    const expenseRuleTypes = [
        {value: "category_match", label: data.t.read("expense_rules.category_match")},
        {value: "mileage_rate_threshold", label: data.t.read("expense_rules.mileage_rate_threshold")},
        {value: "receipt_missing", label: data.t.read("expense_rules.receipt_missing")},
        {value: "category_spend_limit", label: data.t.read("category_spend_limit")},
        {value: "max_tip_percentage", label: data.t.read("max_tip_percentage")},
        {value: "travel_expense_limit", label: data.t.read("travel_expense_limit")},
        ...employeeRankOption,
    ];
    const accountRuleTypes = [
        {value: "monthly_submitted_report_count", label: data.t.read("expense_rules.monthly_submitted_report_count")},
        {value: "expense_account_amount_threshold", label: data.t.read("expense_rules.expense_account_amount_threshold")},
        {value: "employee_number_required", label: data.t.read("expense_rules.employee_number_required")},
        ...employeeRankOption,
    ];

    const allEffectOptions = [
        {value: "exception_required", label: data.t.read("expense_rules.effect_exception_required")},
        {value: "block", label: data.t.read("expense_rules.effect_block")},
        {value: "warning", label: data.t.read("expense_rules.effect_warning")},
    ];

    const scopeOptions = [
        {value: "expense", label: data.t.read("expense_rules.scope_expense")},
        {value: "expense_account", label: data.t.read("expense_rules.scope_expense_account")},
    ];

    const phaseOptions = [
        {value: "pre_submit", label: data.t.read("expense_rules.phase_pre_submit")},
    ];
    const comparisonOptions = COMPARISON_OPERATORS.map(operator => ({
        value: operator,
        label: data.t.read(`expense_rules.operator_${operator}`),
    }));
    const ruleTypes = (localRule.scope || EXPENSE_SCOPE) === EXPENSE_ACCOUNT_SCOPE ? accountRuleTypes : expenseRuleTypes;
    const effectOptions = (localRule.scope || EXPENSE_SCOPE) === EXPENSE_ACCOUNT_SCOPE
        ? allEffectOptions.filter(option => option.value !== "exception_required")
        : allEffectOptions;

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? "#F39800" : "#d6dae3",
            boxShadow: state.isFocused ? "0 0 0 1px rgba(243, 152, 0, 0.2)" : "none",
            minHeight: "38px",
        }),
        menuPortal: (base) => ({...base, zIndex: 9999}),
    };

    const buildImputationOptions = () => {
        const byId = {};
        const childrenByParent = {};

        (clientImputations || []).forEach((row) => {
            const id = imputationDocumentId(row);
            if (!id) return;
            byId[id] = row;
        });

        (clientImputations || []).forEach((row) => {
            const parentId = row.parent_id ? String(row.parent_id) : null;
            if (!parentId) return;
            if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
            childrenByParent[parentId].push(row);
        });

        const rootRows = (clientImputations || []).filter((row) => {
            const parentId = row.parent_id ? String(row.parent_id) : null;
            return !parentId || !byId[parentId];
        });

        return rootRows.flatMap((parent) => {
            const parentId = imputationDocumentId(parent);
            const parentName = categoryLabel(parent.category);
            const children = childrenByParent[parentId] || [];
            const parentOption = {
                value: parentId,
                label: children.length > 0
                    ? `${parentName} (${readLabel("expense_rules.all_subcategories", "all sub-categories")})`
                    : parentName,
                category: parent.category,
                imputation_id: parentId,
                imputation_level: IMPUTATION_LEVEL_PARENT,
                parent_category: parent.category,
            };
            const childOptions = children.map((child) => {
                const childId = imputationDocumentId(child);
                return {
                    value: childId,
                    label: `${parentName} / ${categoryLabel(child.category)}`,
                    category: child.category,
                    imputation_id: childId,
                    imputation_level: IMPUTATION_LEVEL_CHILD,
                    parent_imputation_id: parentId,
                    parent_category: parent.category,
                };
            });
            return [parentOption, ...childOptions];
        });
    };

    const imputationOptions = buildImputationOptions();
    const imputationOptionsById = imputationOptions.reduce((accumulator, option) => {
        accumulator[option.value] = option;
        return accumulator;
    }, {});

    const selectedImputationOptions = (parameters = {}) => resolveImputationSelectOptions(
        parameters,
        imputationOptionsById,
        { orphanLabel: readLabel("expense_rules.imputation_target_orphan", "deleted") },
    );

    const imputationTargetFromOption = (option) => ({
        category: option?.category,
        imputation_id: option?.imputation_id,
        imputation_level: option?.imputation_level,
        parent_imputation_id: option?.parent_imputation_id,
        parent_category: option?.parent_category,
    });

    const updateConditionAt = (index, updater) => {
        const next = [...(localRule.conditions || [])];
        next[index] = updater(next[index]);
        setSelectedRule({...localRule, conditions: next});
    };

    const updateConditionParameters = (index, parametersUpdate) => {
        updateConditionAt(index, condition => ({
            ...condition,
            parameters: {...(condition.parameters || {}), ...parametersUpdate},
        }));
    };

    const removeConditionAt = (index) => {
        setSelectedRule({
            ...localRule,
            conditions: (localRule.conditions || []).filter((_, i) => i !== index),
        });
    };

    const addCondition = () => {
        setSelectedRule({
            ...localRule,
            conditions: [...(localRule.conditions || []), createEmptyCondition(localRule.scope || EXPENSE_SCOPE)],
        });
    };

    const iconForType = (type) => {
        if (type === "max_tip_percentage") return faPercent;
        if (type === "travel_expense_limit") return faPlane;
        if (type === "employee_rank") return faUserTie;
        return faDollarSign;
    };

    const labelForType = (type) => {
        const found = [...expenseRuleTypes, ...accountRuleTypes].find(r => r.value === type);
        return found ? found.label : type;
    };

    const updateRule = (patch) => {
        setSelectedRule({...localRule, ...patch});
    };

    const renderConditionParameters = (condition, index) => {
        if (["category_spend_limit", "category_match"].includes(condition.type)) {
            const selectedOptions = selectedImputationOptions(condition.parameters);
            const categorySelectOptions = [
                ...imputationOptions,
                ...selectedOptions.filter(option => option.isOrphan),
            ];
            const {taxBasis, includeTip} = amountBasisToUi(condition.parameters?.amount_basis);
            const taxBasisOptions = [
                {value: TAX_BASIS_PRETAX, label: data.t.read("expense_rules.amount_basis_tax_pretax")},
                {value: TAX_BASIS_TAX_INCLUDED, label: data.t.read("expense_rules.amount_basis_tax_included")},
            ];
            const includeTipOptions = [
                {value: "no", label: data.t.read("expense_rules.amount_basis_include_tip_no")},
                {value: "yes", label: data.t.read("expense_rules.amount_basis_include_tip_yes")},
            ];
            return (
                <div className="row">
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("category")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`category-select-${index}`}
                            value={selectedOptions}
                            options={categorySelectOptions}
                            isMulti
                            classNamePrefix="select"
                            onChange={choices => {
                                const selectedChoices = (Array.isArray(choices) ? choices : [])
                                    .filter(choice => !choice?.isOrphan);
                                const targets = selectedChoices.map(imputationTargetFromOption);
                                const firstTarget = targets[0] || {};
                                updateConditionParameters(index, {
                                    imputation_targets: targets,
                                    category: firstTarget.category,
                                    imputation_id: firstTarget.imputation_id,
                                    imputation_level: firstTarget.imputation_level,
                                    parent_imputation_id: firstTarget.parent_imputation_id,
                                    parent_category: firstTarget.parent_category,
                                });
                            }}
                        />
                        {condition.type === "category_spend_limit" && <>
                        <label className="d-block mb-1 mt-3">{data.t.read("expense_rules.amount_basis_tax")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`amount-basis-tax-${index}`}
                            value={taxBasisOptions.find(option => option.value === taxBasis) || taxBasisOptions[0]}
                            options={taxBasisOptions}
                            classNamePrefix="select"
                            onChange={choice => {
                                const nextTaxBasis = choice?.value || TAX_BASIS_PRETAX;
                                updateConditionParameters(index, {
                                    amount_basis: uiToAmountBasis(nextTaxBasis, includeTip),
                                });
                            }}
                        />
                        </>}
                    </div>
                    {condition.type === "category_spend_limit" && <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("limit_amount")}</label>
                        <MoneyInput
                            className={"p-2 border rounded-lg expense-rule-edit-modal__limit-input"}
                            value={condition.parameters?.limit ?? 0}
                            onChange={(value) => updateConditionParameters(index, {limit: value})}
                            locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                            placeholder={data.t.read("rule.amount")}
                        />
                        <label className="d-block mb-1 mt-3">{data.t.read("expense_rules.amount_basis_include_tip")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`amount-basis-tip-${index}`}
                            className="expense-rule-edit-modal__include-tip-select"
                            classNamePrefix="select"
                            value={includeTipOptions.find(option => option.value === (includeTip ? "yes" : "no")) || includeTipOptions[0]}
                            options={includeTipOptions}
                            onChange={choice => {
                                const nextIncludeTip = choice?.value === "yes";
                                updateConditionParameters(index, {
                                    amount_basis: uiToAmountBasis(taxBasis, nextIncludeTip),
                                });
                            }}
                        />
                    </div>}
                </div>
            );
        }

        if (condition.type === "max_tip_percentage") {
            return (
                <div>
                    <label className="d-block mb-1">{data.t.read("max_tip_percentage")}</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        className="form-control expense-rule-edit-modal__tip-input"
                        aria-label={data.t.read("max_tip_percentage")}
                        value={condition.parameters?.maxPercent != null ? condition.parameters.maxPercent * 100 : ""}
                        onChange={e => {
                            const pct = parseFloat(e.target.value);
                            updateConditionParameters(index, {maxPercent: isNaN(pct) ? "" : pct / 100});
                        }}
                        placeholder={data.t.read("expense_rules.tip_percentage_placeholder")}
                    />
                    <label className="d-block mb-1 mt-3">{data.t.read("expense_rules.tip_basis")}</label>
                    <Select
                        name={`tip-basis-${index}`}
                        styles={selectStyles}
                        classNamePrefix="select"
                        options={["pretax", "tax_included"].map(value => ({value, label: data.t.read(`expense_rules.tip_basis_${value}`)}))}
                        value={{value: condition.parameters?.amount_basis || "tax_included", label: data.t.read(`expense_rules.tip_basis_${condition.parameters?.amount_basis || "tax_included"}`)}}
                        onChange={choice => updateConditionParameters(index, {amount_basis: choice.value})}
                    />
                </div>
            );
        }

        if (condition.type === "receipt_missing") return null;

        if (condition.type === "mileage_rate_threshold") {
            return <div className="row">
                <div className="col-md-6 mb-2">
                    <label className="d-block mb-1">{data.t.read("expense_rules.mileage_rate_limit")}</label>
                    <input type="number" min="0" step="0.01" className="form-control"
                        aria-label={data.t.read("expense_rules.mileage_rate_limit")}
                        value={condition.parameters?.limit ?? ""}
                        onChange={e => updateConditionParameters(index, {limit: e.target.value === "" ? "" : Number(e.target.value), distance_unit: "km"})} />
                </div>
                <div className="col-md-6 mb-2">
                    <label className="d-block mb-1">{data.t.read("currency")}</label>
                    <input className="form-control" maxLength={3}
                        aria-label={data.t.read("currency")}
                        value={condition.parameters?.currency || ""}
                        onChange={e => updateConditionParameters(index, {currency: e.target.value.toUpperCase(), distance_unit: "km"})} />
                </div>
            </div>;
        }

        if (condition.type === "travel_expense_limit") {
            return (
                <div className="row">
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("location")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`location-select-${index}`}
                            value={{value: condition.parameters?.location, label: condition.parameters?.location}}
                            options={[
                                {value: "All", label: "All"},
                                {value: "Canada", label: "Canada"},
                                {value: "USA", label: "USA"},
                            ]}
                            classNamePrefix="select"
                            onChange={choice => updateConditionParameters(index, {location: choice?.value})}
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("limit_amount")}</label>
                        <MoneyInput
                            className={"p-2 border rounded-lg"}
                            value={condition.parameters?.limit ?? 0}
                            onChange={(value) => updateConditionParameters(index, {limit: value})}
                            locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                            placeholder={"$ " + data.t.read("rule.amount")}
                        />
                    </div>
                </div>
            );
        }

        if (condition.type === "monthly_submitted_report_count") {
            return (
                <div className="row">
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("expense_rules.operator")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`comparison-operator-${index}`}
                            value={comparisonOptions.find(option => option.value === condition.parameters?.operator) || null}
                            options={comparisonOptions}
                            classNamePrefix="select"
                            onChange={choice => updateConditionParameters(index, {operator: choice?.value})}
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("expense_rules.report_count_value")}</label>
                        <input
                            type="number"
                            min="0"
                            step="1"
                            className="form-control"
                            aria-label={data.t.read("expense_rules.report_count_value")}
                            value={condition.parameters?.value ?? ""}
                            onChange={e => updateConditionParameters(index, {value: e.target.value === "" ? "" : Number(e.target.value)})}
                        />
                    </div>
                </div>
            );
        }

        if (condition.type === "expense_account_amount_threshold") {
            return (
                <div className="row">
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("expense_rules.operator")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`comparison-operator-${index}`}
                            value={comparisonOptions.find(option => option.value === condition.parameters?.operator) || null}
                            options={comparisonOptions}
                            classNamePrefix="select"
                            onChange={choice => updateConditionParameters(index, {operator: choice?.value})}
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("expense_rules.amount_value")}</label>
                        <MoneyInput
                            className={"p-2 border rounded-lg"}
                            value={condition.parameters?.value ?? 0}
                            onChange={(value) => updateConditionParameters(index, {value})}
                            locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                            placeholder={data.t.read("expense_rules.amount_value")}
                        />
                    </div>
                </div>
            );
        }

        if (condition.type === "employee_rank") {
            const selectedRankIds = Array.isArray(condition.parameters?.rank_ids) ? condition.parameters.rank_ids : [];
            // XPEN-3026-007: follows the in-file Category picker's plain react-select
            // style, but not its resolveImputationSelectOptions/isOrphan machinery —
            // that exists for deleted imputation categories and needs a dedicated
            // "deleted" label. A rank absent from the latest fetch has only gone
            // inactive in Aderant, not been deleted, so a plain raw-id fallback
            // (matching imputation-config-modal.js's renderRankIdsPicker) is the
            // correct, simpler fit here. Do not re-litigate — see XPEN-3243.
            const selectedRankOptions = selectedRankIds.map(id => rankOptionsById[id] || {value: id, label: String(id)});
            // Display Include when mode is missing; do not write mode on render.
            const rankModeOptions = [
                {value: "include", label: data.t.read("expense_rules.rank_mode_include")},
                {value: "exclude", label: data.t.read("expense_rules.rank_mode_exclude")},
            ];
            const selectedRankMode = condition.parameters?.mode === "exclude" ? "exclude" : "include";
            return (
                <div className="row">
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("expense_rules.rank_mode")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`employee-rank-mode-${index}`}
                            value={rankModeOptions.find(option => option.value === selectedRankMode) || null}
                            options={rankModeOptions}
                            classNamePrefix="select"
                            onChange={choice => updateConditionParameters(index, {mode: choice?.value})}
                        />
                    </div>
                    <div className="col-md-6 mb-2">
                        <label className="d-block mb-1">{data.t.read("expense_rules.employee_rank")}</label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                            name={`employee-rank-select-${index}`}
                            value={selectedRankOptions}
                            options={rankOptions}
                            isMulti
                            classNamePrefix="select"
                            placeholder={data.t.read("imputation_ranks_placeholder")}
                            onChange={choices => {
                                const rankIds = (Array.isArray(choices) ? choices : []).map(choice => choice.value);
                                updateConditionParameters(index, {rank_ids: rankIds});
                            }}
                        />
                    </div>
                </div>
            );
        }
        return null;
    };

    const logicValue = localRule.condition_logic === "any" ? "any" : "all";

    return (
        <form onSubmit={onSubmit}>
            <Modal
                title={selectedRule._id ? data.t.read("edit_rule") : data.t.read("add_rule")}
                okIsSubmit={true}
                cancelAction={onCancel}
                okText={data.t.read("save")}
                cancelText={data.t.read("cancel")}
                width="760px"
            >
                <div className="px-1 pt-1">
                    <div className="row mb-3">
                        <div className="col-md-9">
                            <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("name")}</label>
                            <input
                                type="text"
                                aria-label={data.t.read("name")}
                                required
                                className="form-control"
                                placeholder={data.t.read("expense_rules.rule_name_placeholder")}
                                value={localRule.name || ""}
                                onChange={(e) => updateRule({name: e.target.value})}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("active")}</label>
                            <div className="pt-1">
                                <Switch
                                    checked={!!localRule.active}
                                    onChange={(active) => updateRule({active})}
                                    width={62}
                                    height={28}
                                    onColor="#F39800"
                                    onHandleColor="#ffffff"
                                    offColor="#D1D5DB"
                                    boxShadow="0 1px 3px rgba(15, 23, 42, 0.18)"
                                    activeBoxShadow="0 0 0 2px rgba(243, 152, 0, 0.15)"
                                    id="expense-rule-active-switch"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("description")}</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder={data.t.read("expense_rules.rule_description_placeholder")}
                            value={localRule.description || ""}
                            onChange={e => updateRule({description: e.target.value})}
                        />
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-4">
                            <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("expense_rules.scope")}</label>
                            <Select
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                name="scope-select"
                                value={scopeOptions.find(o => o.value === (localRule.scope || EXPENSE_SCOPE))}
                                options={scopeOptions}
                                classNamePrefix="select"
                                onChange={choice => {
                                    const nextScope = choice?.value || EXPENSE_SCOPE;
                                    const nextRuleTypes = nextScope === EXPENSE_ACCOUNT_SCOPE ? accountRuleTypes : expenseRuleTypes;
                                    const nextConditions = (localRule.conditions || []).every(condition =>
                                        nextRuleTypes.some(ruleType => ruleType.value === condition.type)
                                    ) ? (localRule.conditions || []) : [createEmptyCondition(nextScope)];
                                    updateRule({
                                        scope: nextScope,
                                        conditions: nextConditions,
                                        effect: nextScope === EXPENSE_ACCOUNT_SCOPE && localRule.effect === "exception_required"
                                            ? "block"
                                            : localRule.effect,
                                    });
                                }}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("expense_rules.phase")}</label>
                            <Select
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                name="phase-select"
                                value={phaseOptions.find(o => o.value === (localRule.phase || "pre_submit"))}
                                options={phaseOptions}
                                classNamePrefix="select"
                                onChange={choice => updateRule({phase: choice?.value})}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("expense_rules.effect")}</label>
                            <Select
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                name="effect-select"
                                value={effectOptions.find(o => o.value === localRule.effect)}
                                options={effectOptions}
                                classNamePrefix="select"
                                onChange={choice => updateRule({effect: choice?.value})}
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("expense_rules.condition_logic")}</label>
                        <div className="expense-rule-edit-modal__logic-group d-inline-flex overflow-hidden rounded">
                            <button
                                type="button"
                                onClick={() => updateRule({condition_logic: "all"})}
                                className={`expense-rule-edit-modal__logic-button expense-rule-edit-modal__logic-button--left ${logicValue === "all" ? "expense-rule-edit-modal__logic-button--active" : ""}`}
                            >{data.t.read("expense_rules.condition_logic_all_short")}</button>
                            <button
                                type="button"
                                onClick={() => updateRule({condition_logic: "any"})}
                                className={`expense-rule-edit-modal__logic-button expense-rule-edit-modal__logic-button--right ${logicValue === "any" ? "expense-rule-edit-modal__logic-button--active" : ""}`}
                            >{data.t.read("expense_rules.condition_logic_any_short")}</button>
                        </div>
                    </div>

                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <label className="small font-weight-bold text-uppercase d-block mb-0">{data.t.read("expense_rules.conditions")}</label>
                            <button
                                type="button"
                                onClick={addCondition}
                                className="btn btn-link btn-sm p-0 font-weight-bold text-primary"
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-1"/> {data.t.read("expense_rules.add_condition")}
                            </button>
                        </div>

                        {(localRule.conditions || []).map((condition, index) => (
                            <div key={`condition-${index}`} className="expense-rule-edit-modal__condition-card">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div className="d-flex align-items-center">
                                        <span className="expense-rule-edit-modal__condition-icon">
                                            <FontAwesomeIcon icon={iconForType(condition.type)}/>
                                        </span>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={{
                                                ...selectStyles,
                                                control: (base, state) => ({
                                                    ...base,
                                                    border: "none",
                                                    background: "transparent",
                                                    boxShadow: "none",
                                                    minHeight: "32px",
                                                    fontWeight: 600,
                                                    color: "#111827",
                                                }),
                                                indicatorSeparator: () => ({display: "none"}),
                                            }}
                                            value={{value: condition.type, label: labelForType(condition.type)}}
                                            options={ruleTypes}
                                            name={`condition-type-${index}`}
                                            classNamePrefix="select"
                                            onChange={choice => updateConditionAt(index, () => ({
                                                type: choice?.value,
                                                parameters: {},
                                            }))}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeConditionAt(index)}
                                        disabled={(localRule.conditions || []).length <= 1}
                                        className="btn btn-link btn-sm text-muted p-1"
                                    >
                                        <FontAwesomeIcon icon={faTrash}/>
                                    </button>
                                </div>
                                {renderConditionParameters(condition, index)}
                            </div>
                        ))}
                    </div>

                    <div className="mb-2">
                        <label className="small font-weight-bold text-uppercase d-block mb-1">{data.t.read("expense_rules.validation_message")}</label>
                        <textarea
                            aria-label={data.t.read("expense_rules.message")}
                            className="form-control"
                            rows={2}
                            value={localRule.message || ""}
                            onChange={e => updateRule({message: e.target.value})}
                        />
                        <div className="small text-muted mt-1">
                            {data.t.read("expense_rules.validation_message_helper")}
                        </div>
                    </div>

                    {validationError && (
                        <p className="text-danger mt-3 mb-0 expense-rule-edit-modal__validation-error">{validationError}</p>
                    )}
                </div>
            </Modal>
        </form>
    );
};

export default observer(ExpenseRuleEditModal);
